import useWorkspaceViewport from './components/workspace/useWorkspaceViewport';
// Built by Solomon
import React from "react";
import useInitialAccountCheck from "./hooks/useInitialAccountCheck";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import LaunchPage from "./components/LaunchPage";
import Profile from "./components/Profile";
import PublicUserProfile from "./components/PublicUserProfile";
import PublicCommunityInvite from "./components/PublicCommunityInvite";
import PublicCommunitiesDirectory from "./components/PublicCommunitiesDirectory";
import TermsOfService from "./components/Terms";
import PrivacyPolicy from "./components/PrivacyPolicy";
import StudentPrivacyNotice from "./components/StudentPrivacyNotice";
import CopyrightPolicy from "./components/CopyrightPolicy";
import Support from "./components/Support";
import ValentinePage from "./components/ValentinePage";
import ResetPassword from "./components/ResetPassword";
import AuthCallback from "./components/AuthCallback";
import MobileAuthCallback from "./components/MobileAuthCallback";
import MobileAuthRedirect from "./components/MobileAuthRedirect";
import Sessions from "./components/Sessions";
import Leaderboard from "./components/Leaderboard";
import LiveMatches from "./components/LiveMatches";
import InstituteLeaderboard from "./components/InstituteLeaderboard";
import InstitutionCreate from "./components/InstitutionCreate";
import InstitutionLiveDashboard from "./components/InstitutionLiveDashboard";
import ProAmTaxi from "./components/ProAmTaxi";
import RelayForLife from "./components/RelayForLife";
import MoroccoScraper from "./components/morocco";
import CognismScraper from "./components/CognismScraper";
import SnowsProAmCRM from "./components/crm";
import BookingPartners from "./components/BookingPartners";
import SportIrelandSwimming from "./components/SportIrelandSwimming";
import Venues from "./components/Venues";
import VenueJoinFlow from "./components/VenueJoinFlow";
import BusinessLandingPage from "./components/BusinessLandingPages";
import WorkoutClubDashboard from "./components/WorkoutClubDashboard";
import TournamentDirectory from "./components/TournamentDirectory";
import Tournaments from "./components/Tournaments";
import GlobalDrawer from "./components/GlobalDrawer";
import PublicPageMetadata from "./components/PublicPageMetadata";
import AppAnalyticsDashboard from "./components/AppAnalyticsDashboard";
import OutreachUnsubscribe from "./components/OutreachUnsubscribe";
import { GlobalAuthProvider, useAuthModal } from "./AuthModalContext";
import KilcarLodge from "./components/KilcarLodge";

// 👇 add these
import { AuthProvider, useAuth } from "./AuthContext";
import ProtectedRoute from "./ProtectedRoute";
import PageLoading from "./components/PageLoading";
import { supabase } from "./supabaseClient";
import logo from "./assets/logo.png";

// 👇 Toastify
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const WorkspaceProAm = React.lazy(() => import('./components/workspace/WorkspaceProAm'));
function WorkspaceProAmSurface(){const location=useLocation(),inWorkspace=/^\/workspace(?:\/|$)/.test(location.pathname);const [visited,setVisited]=React.useState(inWorkspace);useWorkspaceViewport(inWorkspace);React.useEffect(()=>{if(inWorkspace)setVisited(true);},[inWorkspace]);return visited||inWorkspace?<React.Suspense fallback={null}><WorkspaceProAm/></React.Suspense>:null;}
const ProAmStudioPage = React.lazy(() => import("./components/proamStudio/ProAmStudioPage"));
const AccountManagementPage = React.lazy(() => import("./components/workspace/AccountManagementPage"));
const WorkspaceOutreachPage = React.lazy(() => import("./components/workspace/WorkspaceOutreachPage"));
const WorkspacePage = React.lazy(() => import("./components/WorkspacePage"));
const AppShowcase = React.lazy(() => import("./components/AppShowcase"));
const AppShowcasePage = () => (
  <React.Suspense fallback={<PageLoading />}>
    <AppShowcase />
  </React.Suspense>
);

const SHOW_EVENTS_PAGE = false;

const TournamentInviteRoute = () => {
  const { tournamentName } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.get("view") === "matchups" || params.has("team") || params.has("admin") || params.has("stage") || params.has("overview") || location.hash === "#admin") {
    return <Tournaments />;
  }
  return <TournamentDirectory enrollSlug={tournamentName} invite />;
};

const WorldwideStepsRedirect = () => {
  React.useEffect(() => {
    window.location.replace("https://worldwidesteps.com/");
  }, []);
  return <main style={{ padding: 24 }}><a href="https://worldwidesteps.com/">Continue to World Wide Steps</a></main>;
};

const getSavedTheme = () => {
  try {
    const savedTheme = window.localStorage.getItem("spa-theme");
    if (savedTheme === "dark" || savedTheme === "light") return savedTheme;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  } catch {
    return "dark";
  }
};

// Some established screens own their theme toggle locally.  This bridge makes
// those existing toggles update the document-level preference immediately, so
// every route sees the same theme without forcing a page reload.
const AppThemeSync = ({ children }) => {
  const location = useLocation();
  const [theme, setTheme] = React.useState(getSavedTheme);

  React.useEffect(() => {
    const applySavedTheme = () => setTheme(getSavedTheme());
    const originalSetItem = Storage.prototype.setItem;

    Storage.prototype.setItem = function setItemWithThemeNotification(
      key,
      value,
    ) {
      originalSetItem.call(this, key, value);
      if (key === "spa-theme")
        window.dispatchEvent(new Event("spa-theme-change"));
    };

    window.addEventListener("spa-theme-change", applySavedTheme);
    window.addEventListener("storage", applySavedTheme);
    return () => {
      Storage.prototype.setItem = originalSetItem;
      window.removeEventListener("spa-theme-change", applySavedTheme);
      window.removeEventListener("storage", applySavedTheme);
    };
  }, []);

  React.useEffect(() => {
    // Covers a preference changed on a route that has its own legacy toggle.
    setTheme(getSavedTheme());
  }, [location.pathname]);

  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  return children;
};

const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading: authLoading, accountSuspension, suspensionLoading, suspensionError, refreshSuspension, accountWarning, warningLoading, warningError, refreshWarning } = useAuth();
  const initialAccountCheck = useInitialAccountCheck({ userId: user?.id, authLoading, suspensionLoading, warningLoading, suspensionError, warningError });
  const { openAuthModal } = useAuthModal();
  const [accessActionBusy, setAccessActionBusy] = React.useState(false);
  const [accessActionError, setAccessActionError] = React.useState("");
  const scrollPosRef = React.useRef({});
  const isBackNav = React.useRef(false);
  const memberName = [profile?.name, profile?.surname].filter(Boolean).join(" ") || profile?.username || user?.user_metadata?.full_name || user?.user_metadata?.name || "Account member";
  const memberUsername = profile?.username || user?.user_metadata?.username || "";
  const memberPhoto = profile?.profile_img || user?.user_metadata?.avatar_url || user?.user_metadata?.picture || "";

  const protectedAuthRequest = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const stateRequested = location.state?.auth === "signin";
    const queryRequested = params.get("auth") === "signin";
    const raw = stateRequested
      ? String(location.state?.redirect || "")
      : queryRequested ? params.get("redirect") || "" : "";
    if (!raw.startsWith("/") || raw.startsWith("//")) {
      return { redirect: "", shouldClean: stateRequested || queryRequested };
    }
    try {
      const parsed = new URL(raw, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        return { redirect: "", shouldClean: stateRequested || queryRequested };
      }
      return {
        redirect: `${parsed.pathname}${parsed.search}${parsed.hash}`,
        shouldClean: stateRequested || queryRequested,
      };
    } catch {
      return { redirect: "", shouldClean: stateRequested || queryRequested };
    }
  }, [location.search, location.state]);

  React.useEffect(() => {
    const hash = window.location.hash;
    const search = new URLSearchParams(window.location.search);
    const recoveryQuery = search.get("type") === "recovery" &&
      (search.has("code") || search.has("token_hash"));
    if (
      ((hash.includes("access_token") && hash.includes("type=recovery")) || recoveryQuery) &&
      !window.location.pathname.includes("/reset-password")
    ) {
      navigate("/reset-password" + window.location.search + hash, { replace: true });
    }
  }, [navigate]);

  // Supabase can consume a PKCE recovery code before this route reads the URL.
  // The PASSWORD_RECOVERY event is the reliable signal that the user should
  // land on the password form, even when the callback URL was the site root.
  React.useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" && window.location.pathname !== "/reset-password") {
        navigate("/reset-password", { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  React.useEffect(() => {
    const protectedRedirect = protectedAuthRequest.redirect;
    if (location.pathname !== "/" || !protectedRedirect || authLoading) return;
    if (user) {
      navigate(protectedRedirect, { replace: true });
      return;
    }
    const isStripeConnectSetup = protectedRedirect.includes(
      "payout_setup=stripe_connect",
    );
    const isSchoolDashboard = protectedRedirect.startsWith(
      "/institutions/schools/",
    );
    openAuthModal({
      mode: "signin",
      redirectTo: protectedRedirect,
      title: isStripeConnectSetup
        ? "Continue setting up Stripe Connect"
        : isSchoolDashboard
        ? "Sign in to view your school dashboard"
        : "Sign in to open your profile",
      body: isStripeConnectSetup
        ? "Sign in to your Snows ProAm account to continue cash payout setup. After sign-in, you will see the Stripe Connect setup steps."
        : isSchoolDashboard
        ? "Log in with your Snows ProAm account. After sign-in, you’ll go straight to your school dashboard."
        : "This page is private. Sign in and we’ll return you to where you were.",
    });
    if (protectedAuthRequest.shouldClean) {
      navigate("/", { replace: true, state: null });
    }
  }, [
    authLoading,
    location.pathname,
    navigate,
    openAuthModal,
    protectedAuthRequest,
    user,
  ]);

  // Detect back/forward navigation
  React.useEffect(() => {
    const handlePopState = () => {
      isBackNav.current = true;
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Save scroll position continuously for current page
  React.useEffect(() => {
    const handleScroll = () => {
      scrollPosRef.current[location.pathname] = window.scrollY;
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    return () =>
      window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [location.pathname]);

  // On route change: save current, restore destination
  React.useEffect(() => {
    const savedPos = scrollPosRef.current[location.pathname];

    if (isBackNav.current && savedPos !== undefined) {
      // Navigating back - restore saved position
      window.scrollTo(0, savedPos);
    } else {
      // Fresh navigation - scroll to top
      window.scrollTo(0, 0);
    }

    // Reset flag after scroll is applied
    const timer = setTimeout(() => {
      isBackNav.current = false;
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  if (initialAccountCheck && suspensionLoading) return <PageLoading label="Checking account access" />;

  if (initialAccountCheck && !suspensionLoading && warningLoading) return <PageLoading label="Checking account access" />;

  if (user && !authLoading && (accountSuspension || suspensionError)) {
    const checkingFailed = Boolean(suspensionError) && !accountSuspension;
    const isBan = accountSuspension?.restriction_type === "ban";
    const cleanUsername = memberUsername.replace(/^@/, "");
    const supportDetails = [
      `Name: ${memberName}`,
      cleanUsername ? `Username: @${cleanUsername}` : null,
      user?.email ? `Email: ${user.email}` : null,
      "Please review my account access.",
    ].filter(Boolean).join("\n");
    const supportHref = `mailto:support@snowsproam.com?subject=${encodeURIComponent("Account access review")}&body=${encodeURIComponent(supportDetails)}`;
    const restrictionDate = accountSuspension?.expires_at ? new Date(accountSuspension.expires_at) : null;
    const expiresLabel = restrictionDate && !Number.isNaN(restrictionDate.getTime()) ? restrictionDate.toLocaleString() : null;
    const startDate = accountSuspension?.suspended_at ? new Date(accountSuspension.suspended_at) : null;
    const startedLabel = startDate && !Number.isNaN(startDate.getTime()) ? startDate.toLocaleString() : null;
    const retryAccessCheck = async () => {
      if (accessActionBusy) return;
      setAccessActionBusy(true);
      setAccessActionError("");
      try {
        await refreshSuspension();
      } catch {
        setAccessActionError("We still can’t confirm your access. Check your connection and try again.");
      } finally {
        setAccessActionBusy(false);
      }
    };
    const signOutOfRestrictedAccount = async () => {
      if (accessActionBusy) return;
      setAccessActionBusy(true);
      setAccessActionError("");
      try {
        const { error } = await supabase.auth.signOut();
        if (error) setAccessActionError("We couldn’t sign you out. Please try again.");
      } catch {
        setAccessActionError("We couldn’t sign you out. Please try again.");
      } finally {
        setAccessActionBusy(false);
      }
    };
    return (
      <main role="main" aria-labelledby="account-access-title" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 20px", background: "#F4F7FB", color: "#132238" }}>
        <div style={{ width: "min(100%, 540px)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, margin: "0 4px 18px" }}>
            <img src={logo} alt="" style={{ width: 36, height: 36, objectFit: "contain" }} />
            <span><strong style={{ display: "block", fontSize: 15 }}>Snows ProAm</strong><span style={{ display: "block", color: "#687B92", fontSize: 10, fontWeight: 800, letterSpacing: 1.1, marginTop: 2 }}>ACCOUNT ACCESS</span></span>
          </div>
          <section aria-labelledby="account-access-title" style={{ padding: 22, background: "#FFFFFF", border: "1px solid #E0E7F0", borderRadius: 22, boxShadow: "0 8px 24px rgba(23,52,90,.06)" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: 18, background: checkingFailed ? "#FFF8E8" : "#F1F7FF", border: `1px solid ${checkingFailed ? "#F3E1AD" : "#D8E8FA"}`, borderRadius: 16, textAlign: "center" }}>
              <span aria-hidden="true" style={{ width: 42, height: 42, flex: "0 0 42px", display: "grid", placeItems: "center", borderRadius: 13, background: "#FFFFFF", color: checkingFailed ? "#805600" : "#1268C4", border: `1px solid ${checkingFailed ? "#F3E1AD" : "#D8E8FA"}`, fontSize: 22, fontWeight: 700 }}>{checkingFailed ? "!" : isBan ? "×" : "Ⅱ"}</span>
              <div style={{ width: "100%" }}>
                <p style={{ color: checkingFailed ? "#805600" : "#1268C4", fontSize: 11, fontWeight: 800, letterSpacing: 1, margin: "1px 0 5px" }}>{checkingFailed ? "CONNECTION CHECK" : isBan ? "ACCOUNT BANNED" : "ACCESS PAUSED"}</p>
                <h1 id="account-access-title" style={{ fontSize: 25, lineHeight: 1.22, margin: 0, textAlign: "center" }}>{checkingFailed ? "We couldn’t confirm your access" : isBan ? "Your account is banned" : "Your account is paused"}</h1>
              </div>
            </div>
            <p style={{ color: "#42536A", lineHeight: 1.6, textAlign: "center", margin: "16px 0 18px" }}>{checkingFailed ? "Reconnect and try again. We need to confirm your account status before opening Snows ProAm." : "You can’t use Snows ProAm while this restriction is active."}</p>
            {!checkingFailed && <>
              <section aria-labelledby="suspension-reason-title" style={{ background: "#F7F9FC", border: "1px solid #E3E9F1", borderRadius: 15, padding: 16, textAlign: "center" }}>
                <h2 id="suspension-reason-title" style={{ color: "#526B87", fontSize: 11, letterSpacing: .8, margin: "0 0 8px" }}>REASON PROVIDED</h2>
                <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, margin: 0 }}>{accountSuspension.reason || "Please contact support for details."}</p>
              </section>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 14, border: "1px solid #E3E9F1", borderRadius: 15, marginTop: 12, textAlign: "center" }}>
                <span aria-hidden="true" style={{ position: "relative", width: 48, height: 48, flex: "0 0 48px", display: "grid", placeItems: "center", overflow: "hidden", borderRadius: "50%", background: "#EAF3FF", color: "#1268C4", fontSize: 18, fontWeight: 800 }}>
                  {memberName.trim().charAt(0).toUpperCase()}
                  {memberPhoto ? <img src={memberPhoto} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                </span>
                <span style={{ minWidth: 0 }}><span style={{ display: "block", color: "#718198", fontSize: 10, fontWeight: 800, letterSpacing: 1, marginBottom: 3 }}>SIGNED IN AS</span><strong style={{ display: "block", fontSize: 15 }}>{memberName}</strong><span style={{ display: "block", marginTop: 2, color: "#526B87", fontSize: 13 }}>{cleanUsername ? `@${cleanUsername}` : "Snows ProAm member"}</span></span>
              </div>
              <div style={{ marginTop: 14, textAlign: "center" }}>
                <p style={{ color: "#526B87", fontSize: 11, fontWeight: 800, letterSpacing: .8, margin: "0 0 5px" }}>WHAT HAPPENS NEXT</p>
                <p style={{ color: "#42536A", lineHeight: 1.5, fontSize: 14, margin: 0 }}>{isBan ? "This ban remains until an admin restores access." : expiresLabel ? `Your access is scheduled to resume ${expiresLabel}.` : "Your access remains paused until an admin restores it."}</p>
              </div>
              {startedLabel ? <p style={{ color: "#718198", fontSize: 12, textAlign: "center", margin: "10px 0 0" }}>Restriction started {startedLabel}</p> : null}
            </>}
            {accessActionError ? <p role="alert" style={{ color: "#A12B2B", lineHeight: 1.5, fontSize: 14, margin: "14px 0 0" }}>{accessActionError}</p> : null}
            {checkingFailed ? <button type="button" onClick={retryAccessCheck} disabled={accessActionBusy} aria-busy={accessActionBusy} style={{ width: "100%", minHeight: 50, marginTop: 20, background: "#1268C4", color: "#fff", border: 0, borderRadius: 13, fontWeight: 800, cursor: accessActionBusy ? "wait" : "pointer" }}>{accessActionBusy ? "Checking…" : "Try again"}</button> : <a href={supportHref} style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 50, marginTop: 20, padding: "0 16px", background: "#1268C4", color: "#fff", borderRadius: 13, fontWeight: 800, textDecoration: "none" }}>Contact support</a>}
            {checkingFailed ? <p style={{ textAlign: "center", margin: "12px 0 0", fontSize: 13 }}><a href={supportHref} style={{ color: "#1268C4", fontWeight: 700 }}>Email support@snowsproam.com</a></p> : null}
            <button type="button" onClick={signOutOfRestrictedAccount} disabled={accessActionBusy} style={{ display: "block", minHeight: 48, margin: "4px auto 0", padding: "0 18px", background: "transparent", color: "#526B87", border: 0, borderRadius: 12, fontWeight: 700, cursor: accessActionBusy ? "wait" : "pointer" }}>{accessActionBusy ? "Please wait…" : "Sign out"}</button>
          </section>
        </div>
      </main>
    );
  }

  if (user && !authLoading && (accountWarning || warningError)) {
    const checkingFailed = Boolean(warningError) && !accountWarning;
    const runWarningAction = async (action) => {
      if (accessActionBusy) return;
      setAccessActionBusy(true);
      setAccessActionError("");
      try {
        await action();
      } catch {
        setAccessActionError("We couldn’t save your response. Check your connection and try again.");
      } finally {
        setAccessActionBusy(false);
      }
    };
    const retryWarning = () => runWarningAction(async () => {
      const access = await refreshSuspension();
      if (!access?.active && !access?.error) await refreshWarning();
    });
    const continueToApp = () => runWarningAction(async () => {
      const { error } = await supabase.functions.invoke("admin_billing_actions", {
        body: { action: "acknowledge_my_account_warning", warning_id: accountWarning?.id },
      });
      if (error) throw error;
      const access = await refreshSuspension();
      if (!access?.active && !access?.error) await refreshWarning();
    });
    const signOutFromWarning = () => runWarningAction(async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    });
    return (
      <main role="main" aria-labelledby="account-warning-title" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 20px", background: "#F4F7FB", color: "#132238" }}>
        <div style={{ width: "min(100%, 540px)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, margin: "0 4px 18px" }}>
            <img src={logo} alt="" style={{ width: 36, height: 36, objectFit: "contain" }} />
            <span><strong style={{ display: "block", fontSize: 15 }}>Snows ProAm</strong><span style={{ display: "block", color: "#687B92", fontSize: 10, fontWeight: 800, letterSpacing: 1.1, marginTop: 2 }}>ACCOUNT NOTICE</span></span>
          </div>
          <section aria-labelledby="account-warning-title" style={{ padding: 22, background: "#FFFFFF", border: "1px solid #E0E7F0", borderRadius: 22, boxShadow: "0 8px 24px rgba(23,52,90,.06)" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: 18, background: checkingFailed ? "#FFF8E8" : "#F1F7FF", border: `1px solid ${checkingFailed ? "#F3E1AD" : "#D8E8FA"}`, borderRadius: 16, textAlign: "center" }}>
              <span aria-hidden="true" style={{ width: 42, height: 42, display: "grid", placeItems: "center", borderRadius: 13, background: "#FFFFFF", color: checkingFailed ? "#805600" : "#1268C4", border: `1px solid ${checkingFailed ? "#F3E1AD" : "#D8E8FA"}`, fontSize: 22, fontWeight: 700 }}>{checkingFailed ? "!" : "!"}</span>
              <div><p style={{ color: checkingFailed ? "#805600" : "#1268C4", fontSize: 11, fontWeight: 800, letterSpacing: 1, margin: "1px 0 5px" }}>{checkingFailed ? "CONNECTION CHECK" : "ACCOUNT WARNING"}</p><h1 id="account-warning-title" style={{ fontSize: 25, lineHeight: 1.22, margin: 0 }}>{checkingFailed ? "We couldn’t load your notice" : "Please review this warning"}</h1></div>
            </div>
            <p style={{ color: "#42536A", lineHeight: 1.6, textAlign: "center", margin: "16px 0 18px" }}>{checkingFailed ? "Reconnect and try again. We need to load your account notice before opening Snows ProAm." : "A report or account concern has been brought to our attention. Please read the reason below."}</p>
            {!checkingFailed ? <>
              <section aria-labelledby="warning-reason-title" style={{ background: "#F7F9FC", border: "1px solid #E3E9F1", borderRadius: 15, padding: 16, textAlign: "center" }}>
                <h2 id="warning-reason-title" style={{ color: "#526B87", fontSize: 11, letterSpacing: .8, margin: "0 0 8px" }}>REASON PROVIDED</h2>
                <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, margin: 0 }}>{accountWarning?.reason || "Please contact support for details."}</p>
              </section>
              <p style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: 14, margin: "12px 0 0", border: "1px solid #D8E8FA", borderRadius: 14, background: "#F1F7FF", color: "#334B66", fontSize: 13, lineHeight: 1.55 }}><span aria-hidden="true">ℹ</span><span>This is a warning. You can continue using the app. Further reports may lead to a temporary pause or a permanent ban.</span></p>
              {accountWarning?.issued_at ? <p style={{ color: "#718198", fontSize: 12, textAlign: "center", margin: "12px 0 0" }}>Issued {new Date(accountWarning.issued_at).toLocaleDateString()}</p> : null}
            </> : null}
            {accessActionError ? <p role="alert" style={{ color: "#A12B2B", lineHeight: 1.5, fontSize: 14, margin: "14px 0 0" }}>{accessActionError}</p> : null}
            <button type="button" onClick={checkingFailed ? retryWarning : continueToApp} disabled={accessActionBusy} aria-busy={accessActionBusy} style={{ width: "100%", minHeight: 50, marginTop: 20, background: "#1268C4", color: "#fff", border: 0, borderRadius: 13, fontWeight: 800, cursor: accessActionBusy ? "wait" : "pointer" }}>{accessActionBusy ? "Saving…" : checkingFailed ? "Try again" : "Continue to app"}</button>
            <button type="button" onClick={signOutFromWarning} disabled={accessActionBusy} style={{ display: "block", minHeight: 48, margin: "4px auto 0", padding: "0 18px", background: "transparent", color: "#526B87", border: 0, borderRadius: 12, fontWeight: 700 }}>{accessActionBusy ? "Please wait…" : "Sign out"}</button>
          </section>
        </div>
      </main>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<AppShowcasePage />} />
      <Route path="/launch" element={<LaunchPage />} />
      <Route path="/app-preview" element={<AppShowcasePage />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route path="/profile/:username" element={<PublicUserProfile />} />
      <Route path="/communities" element={<PublicCommunitiesDirectory />} />
      <Route path="/community/:communitySlug" element={<PublicCommunityInvite />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacypolicy" element={<PrivacyPolicy />} />
      <Route path="/student-privacy" element={<StudentPrivacyNotice />} />
      <Route path="/copyrightpolicy" element={<CopyrightPolicy />} />
      <Route path="/support" element={<Support />} />
      <Route path="/iloveyou" element={<ValentinePage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/mobile-google" element={<MobileAuthRedirect />} />
      <Route path="/auth/mobile-callback" element={<MobileAuthCallback />} />
      <Route path="/sessions" element={<Sessions />} />
      <Route path="/sessions/:param1" element={<Sessions />} />
      <Route path="/sessions/:param1/:param2" element={<Sessions />} />
      <Route path="/sessions/:param1/:param2/:param3" element={<Sessions />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/livematches" element={<LiveMatches />} />
      <Route path="/instituteleaderboard" element={<InstituteLeaderboard />} />
      <Route
        path="/institutions/create"
        element={
          <ProtectedRoute>
            <InstitutionCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/institutedashboard"
        element={<Navigate to="/profile?tab=institutes" replace />}
      />
      <Route
        path="/institutions/live/:slug"
        element={<InstitutionLiveDashboard />}
      />
      <Route
        path="/institutions/schools/:slug"
        element={<InstitutionLiveDashboard />}
      />
      <Route
        path="/institutions/:slug"
        element={<InstitutionLiveDashboard />}
      />
      <Route path="/worldwide-steps" element={<WorldwideStepsRedirect />} />
      <Route
        path="/worldwidesteps"
        element={<WorldwideStepsRedirect />}
      />
      <Route
        path="/snowsproam/worldwide-steps"
        element={<WorldwideStepsRedirect />}
      />
      <Route
        path="/snowsproam/worldwidesteps"
        element={<WorldwideStepsRedirect />}
      />
      <Route path="/proaminsights" element={<WorldwideStepsRedirect />} />
      <Route path="/proam-taxi" element={<ProAmTaxi />} />
      <Route path="/relayforlife" element={<RelayForLife />} />
      <Route path="/morocco" element={<MoroccoScraper />} />
      <Route path="/cognismscraper" element={<CognismScraper />} />
      <Route path="/crm" element={<SnowsProAmCRM />} />
      <Route path="/booking-partners" element={<BookingPartners />} />
      <Route path="/partners" element={<Venues />} />
      <Route path="/venues" element={<Venues />} />
      <Route path="/venues/join" element={<VenueJoinFlow />} />
      <Route
        path="/wellness"
        element={<BusinessLandingPage type="wellness" />}
      />
      <Route path="/tournaments" element={<Navigate to="/tournaments/live" replace />} />
      <Route path="/tournaments/live" element={<TournamentDirectory />} />
      <Route
        path="/events"
        element={
          SHOW_EVENTS_PAGE ? (
            <BusinessLandingPage type="events" />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/coaching"
        element={<BusinessLandingPage type="coaching" />}
      />
      <Route path="/coaching/dashboard" element={<WorkoutClubDashboard />} />
      <Route
        path="/sport-ireland/swimming"
        element={<SportIrelandSwimming />}
      />
      <Route path="/tournaments/:tournamentName" element={<TournamentInviteRoute />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Navigate to={"/workspace/accounts/profiles" + location.search + location.hash} replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AppAnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outreach"
        element={
          <ProtectedRoute>
            <Navigate to={"/workspace/outreach/" + (["inbox","leads","linkedin","warmup","catalog","demos","sequence","launch","performance","templates","settings"].includes(new URLSearchParams(location.search).get("tab")) ? new URLSearchParams(location.search).get("tab") : "campaigns") + location.search + location.hash} replace />
          </ProtectedRoute>
        }
      />
      <Route path="/workspace/accounts/*" element={<ProtectedRoute><React.Suspense fallback={<PageLoading label="Opening account management"/>}><AccountManagementPage/></React.Suspense></ProtectedRoute>}/>
      <Route path="/workspace/outreach/*" element={<ProtectedRoute><React.Suspense fallback={<PageLoading label="Opening outreach"/>}><WorkspaceOutreachPage/></React.Suspense></ProtectedRoute>}/>
      <Route path="/workspace/proam/*" element={<ProtectedRoute><React.Suspense fallback={<PageLoading/>}><ProAmStudioPage/></React.Suspense></ProtectedRoute>}/>
      <Route path="/workspace/*" element={<ProtectedRoute><React.Suspense fallback={<PageLoading label="Opening workspace"/>}><WorkspacePage/></React.Suspense></ProtectedRoute>}/>
      <Route path="/outreach/whiteboard" element={<Navigate to={"/workspace" + location.search + location.hash} replace />} />
      <Route path="/outreach/lists" element={<Navigate to={"/workspace/lists" + location.search + location.hash} replace />} />
      <Route path="/outreach/messages" element={<Navigate to={"/workspace/messages" + location.search + location.hash} replace />} />
      <Route path="/outreach/settings" element={<Navigate to={"/workspace/settings" + location.search + location.hash} replace />} />
      <Route path="/unsubscribe" element={<OutreachUnsubscribe />} />
      <Route path="/b&b" element={<KilcarLodge />} />
    </Routes>
  );
};

const AppShell = () => {
  return (
    <>
      <PublicPageMetadata />
      <AppThemeSync>
        <AuthProvider>
          <GlobalAuthProvider>
            <AppRoutes />
            <GlobalDrawer />
            <WorkspaceProAmSurface />
          </GlobalAuthProvider>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme="colored"
          />
        </AuthProvider>
      </AppThemeSync>
    </>
  );
};

// Keep the established routes and providers together while enabling supported
// navigation blocking for workspace drafts, including browser Back/Forward.
const router = createBrowserRouter([{ path: "*", element: <AppShell /> }]);
const App = () => <RouterProvider router={router} />;

// Built by Solomon

export default App;
